import {
	RiDeleteBinLine,
	RiDownloadLine,
	RiFileLine,
	RiFileTextLine,
	RiImageLine,
	RiUploadLine,
} from "@remixicon/react";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { $api } from "@/api/query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";

export const Route = createFileRoute("/_app/files")({
	component: FilesPage,
});

function formatFileSize(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fileIcon(mimeType: string) {
	if (mimeType.startsWith("image/")) return RiImageLine;
	if (mimeType.startsWith("text/") || mimeType.includes("pdf")) return RiFileTextLine;
	return RiFileLine;
}

function FilesPage() {
	const queryClient = useQueryClient();
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [uploading, setUploading] = useState(false);
	const [deleteTarget, setDeleteTarget] = useState<{
		id: string;
		name: string;
	} | null>(null);
	const [deleting, setDeleting] = useState(false);

	const { data, isLoading } = $api.useQuery("get", "/api/v1/files");

	const files = data?.data ?? [];

	async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
		const file = e.target.files?.[0];
		if (!file) return;

		setUploading(true);
		const toastId = toast.loading(`Uploading "${file.name}"...`);
		try {
			const formData = new FormData();
			formData.append("file", file);
			formData.append("category", "general");

			const res = await fetch("/api/v1/files", {
				method: "POST",
				credentials: "include",
				body: formData,
			});

			if (!res.ok) {
				const data = await res.json().catch(() => null);
				toast.error(data?.detail ?? "Upload failed", { id: toastId });
				return;
			}

			toast.success(`Uploaded "${file.name}"`, { id: toastId });
			await queryClient.invalidateQueries({
				queryKey: ["get", "/api/v1/files"],
			});
		} catch {
			toast.error("Upload failed", { id: toastId });
		} finally {
			setUploading(false);
			if (fileInputRef.current) fileInputRef.current.value = "";
		}
	}

	async function handleDelete() {
		if (!deleteTarget) return;
		setDeleting(true);
		try {
			const res = await fetch(`/api/v1/files/${deleteTarget.id}`, {
				method: "DELETE",
				credentials: "include",
			});

			if (!res.ok) {
				toast.error("Failed to delete file");
				return;
			}

			toast.success(`Deleted "${deleteTarget.name}"`);
			setDeleteTarget(null);
			await queryClient.invalidateQueries({
				queryKey: ["get", "/api/v1/files"],
			});
		} catch {
			toast.error("Failed to delete file");
		} finally {
			setDeleting(false);
		}
	}

	function handleDownload(id: string, fileName: string) {
		const link = document.createElement("a");
		link.href = `/api/v1/files/${id}/download`;
		link.download = fileName;
		link.click();
	}

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-2xl font-bold tracking-tight">Files</h1>
					<p className="text-muted-foreground">Upload and manage files.</p>
				</div>
				<div>
					<input ref={fileInputRef} type="file" className="hidden" onChange={handleUpload} />
					<Button onClick={() => fileInputRef.current?.click()} disabled={uploading}>
						<RiUploadLine className="mr-2 size-4" />
						{uploading ? "Uploading..." : "Upload file"}
					</Button>
				</div>
			</div>

			<Card>
				<CardHeader>
					<CardTitle>All Files</CardTitle>
					<CardDescription>
						{files.length} file{files.length !== 1 ? "s" : ""}
					</CardDescription>
				</CardHeader>
				<CardContent>
					{isLoading ? (
						<div className="space-y-2">
							{[...Array(3)].map((_, i) => (
								<Skeleton key={i} className="h-12 w-full" />
							))}
						</div>
					) : files.length === 0 ? (
						<div className="flex flex-col items-center justify-center py-12">
							<RiFileLine className="size-12 text-muted-foreground/30 mb-3" />
							<p className="text-muted-foreground font-medium">No files yet</p>
							<p className="text-sm text-muted-foreground mt-1">
								Upload your first file to get started.
							</p>
						</div>
					) : (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Name</TableHead>
									<TableHead>Size</TableHead>
									<TableHead>Category</TableHead>
									<TableHead>Uploaded by</TableHead>
									<TableHead>Date</TableHead>
									<TableHead className="w-24" />
								</TableRow>
							</TableHeader>
							<TableBody>
								{files.map((file) => {
									const Icon = fileIcon(file.mimeType);
									return (
										<TableRow key={file.id}>
											<TableCell>
												<div className="flex items-center gap-2">
													<Icon className="size-4 text-muted-foreground" />
													<span className="truncate max-w-[200px]">{file.fileName}</span>
												</div>
											</TableCell>
											<TableCell className="text-muted-foreground">
												{formatFileSize(file.fileSize)}
											</TableCell>
											<TableCell className="text-muted-foreground">{file.category}</TableCell>
											<TableCell className="text-muted-foreground">
												{file.uploadedByName ?? "—"}
											</TableCell>
											<TableCell className="text-muted-foreground">
												{new Date(file.createdAt).toLocaleDateString()}
											</TableCell>
											<TableCell>
												<div className="flex items-center gap-1">
													<Button
														variant="ghost"
														size="icon"
														onClick={() => handleDownload(file.id, file.fileName)}
													>
														<RiDownloadLine className="size-4" />
													</Button>
													<Button
														variant="ghost"
														size="icon"
														onClick={() =>
															setDeleteTarget({
																id: file.id,
																name: file.fileName,
															})
														}
													>
														<RiDeleteBinLine className="size-4 text-destructive" />
													</Button>
												</div>
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					)}
				</CardContent>
			</Card>

			<Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Delete file</DialogTitle>
						<DialogDescription>
							Are you sure you want to delete "{deleteTarget?.name}"? This action cannot be undone.
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="outline" onClick={() => setDeleteTarget(null)}>
							Cancel
						</Button>
						<Button variant="destructive" onClick={handleDelete} disabled={deleting}>
							{deleting ? "Deleting..." : "Delete"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
